"use client"

import { colorPartsOf } from "@v2/lib/colorParts";
import { addToClientCart, removeFromClientCart } from "@/app/store/slices/cart/cartSlice";
import { addToCartThunk, removeFromCartThunk } from "@/app/store/slices/cart/cartThunk";
import { ProductDetailType } from "@/app/types/product-site";
import { useRouter } from "@bprogress/next/app";
import { Box, Button, colors, Dialog, DialogContent, Divider, Fade, IconButton, Modal, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import FlexBox from "@v2/components/styled/FlexBox";
import TextOverflow from "@v2/components/styled/TextOverflow";
import { uiColors } from "@v2/lib/color";
import { useAppDispatch, useAppSelector } from "@v2/lib/redux/hooks";
import { ArrowLeft, BadgeCheck, MessageCircleIcon, MessageSquareIcon, ShieldAlert, X } from "lucide-react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useCallback, useRef, useState } from "react";
import Whatsapp from "@v2/assets/icons/whatsapp_black.svg";
import { ClientCartItemType } from "@/app/types/cart";
import { itemFromProductDetail, trackAddToCart, trackRemoveFromCart } from "@v2/lib/analytics";
import { usePdpVariant } from "../components/PdpVariantContext";
import { variantOptionLabels } from "../utils";

export default function PdpAddToCartHandler({
    showProduct,
    site_product_details,
    brandName,
    siteProductId,
}: {
    showProduct: ProductDetailType,
    site_product_details: ClientCartItemType['product']['site_product_details'],
    brandName?: string | null,
    siteProductId?: number
}) {

    const dispatch = useAppDispatch()
    const router = useRouter()
    const theme = useTheme()
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
    const pathname = usePathname()
    const { variantAxes } = usePdpVariant()

    const { loading: addToCartInProcess, fetchingCart: serverCartIsLoading } = useAppSelector((store) => store.cart);

    const isAuthenticated = useAppSelector(s => s.general.session?.isAuthenticated) ?? false
    // Before the session loads a logged-in user looks like a guest, and the
    // item would land in the guest cart.
    const sessionLoaded = useAppSelector(s => s.general.sessionLoaded)

    const isInCart = isAuthenticated
        ? useAppSelector(s => s.cart.serverCart?.items?.some(i => i.product.product_id === showProduct.product_id))
        : useAppSelector(s => s.cart.clientCart?.items?.some(i => i.product.product_id === showProduct.product_id));

    const showModalTimeout = useRef(null)
    const [addToCartModalShow, setAddToCartModalShow] = useState(false)
    const [replicaLoginDialogOpen, setReplicaLoginDialogOpen] = useState(false)
    const showAddToCartDialog = useCallback((timeout: number = 6000) => {
        clearTimeout(showModalTimeout.current)
        setAddToCartModalShow(true)
        // showModalTimeout.current = setTimeout(() => {
        //     setAddToCartModalShow(false)
        // }, timeout)
    }, [])

    const getInquiry = useCallback(() => {
        const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

        if (!isMobile) return;

        let message = `سلام میخواستم قیمت این محصول رو بدونم`;
        message += `\nکد کالا: ${showProduct.name}`;
        message += `\nکد رنگ: ${showProduct.color_code}`;

        const phoneNumber = '+989104443316';
        const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent);
        const separator = isIOS ? '&' : '?';
        const url = `sms:${phoneNumber}${separator}body=${encodeURIComponent(message)}`;

        window.open(url, '_blank');
    }, [showProduct, pathname]);

    // Built from the variant the page is showing, not from the markup, so the
    // event survives any change to the button.
    const analyticsItem = useCallback(
        () => itemFromProductDetail(showProduct, { brand: brandName, siteProductId }),
        [showProduct, brandName, siteProductId]
    );

    const addToCart = useCallback(async () => {
        if (process.env.NEXT_PUBLIC_IS_REPLICA === "true") {
            setReplicaLoginDialogOpen(true)
            return
        }

        if (!sessionLoaded) return

        //Should update in client cart
        if (!isAuthenticated) {
            const cartProductItem: ClientCartItemType['product'] = { ...showProduct }
            cartProductItem.site_product_details = site_product_details;
            cartProductItem.variant_options = variantOptionLabels(showProduct, variantAxes);
            dispatch(addToClientCart(cartProductItem));
            trackAddToCart(analyticsItem())
            showAddToCartDialog()
            return;
        }

        const success = await dispatch(addToCartThunk(showProduct.product_id));
        // Only report what the server accepted, so a rejected add is not
        // counted as one.
        if (success) {
            trackAddToCart(analyticsItem())
            showAddToCartDialog()
        }
    }, [isAuthenticated, sessionLoaded, showProduct, variantAxes, analyticsItem])

    const removeFromCart = useCallback(() => {

        if (!isAuthenticated) {
            dispatch(removeFromClientCart(showProduct))
        } else {
            dispatch(removeFromCartThunk(showProduct.product_id));
        }

        trackRemoveFromCart(analyticsItem())

    }, [isAuthenticated, showProduct, analyticsItem])

    const goToCart = useCallback(() => {
        setAddToCartModalShow(false)
        router.push('/fa/v2/cart')
    }, [])

    return (
        <>


            <Box sx={{ flex: 1 }}>

                {isInCart && (
                    <FlexBox sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        {isDesktop && (
                            <FlexBox>
                                <BadgeCheck />
                                <Typography component="span" sx={{ ml: 1, fontSize: 14, color: colors.grey[800] }}>
                                    به سبد خرید اضافه شده
                                </Typography>
                            </FlexBox>
                        )}
                        <Fade in={true}>
                            <Button sx={{
                                height: 48,
                                flex: isDesktop ? 'unset' : 1,
                            }} color="secondary" variant="contained" disableElevation onClick={removeFromCart} loading={addToCartInProcess || serverCartIsLoading}>
                                نمی‌خوامش
                            </Button>
                        </Fade>
                    </FlexBox>
                )}

                {!isInCart && !showProduct.need_inquiry && (
                    <Fade in={true}>
                        <Button sx={{
                            width: 1,
                            height: 48,
                        }} color="primary" variant="contained"
                            disableElevation onClick={addToCart} loading={addToCartInProcess || serverCartIsLoading || !sessionLoaded}>
                            {showProduct.need_inquiry ? "استعلام از طریق پیامک" : "افزودن به سبد خرید"}
                        </Button>
                    </Fade>
                )}

                {showProduct.need_inquiry && (
                    <Fade in={true}>
                        <Button sx={{
                            width: 1,
                            height: 48,
                            color: '#fff',
                            bgcolor: '#0084FF',
                        }} color="primary"
                            variant="contained"
                            disableElevation
                            endIcon={
                                <MessageSquareIcon color="#fff" />
                                // <Image src={Whatsapp} alt="Contact us with Whatsapp" height={32} width={32} />
                            }
                            onClick={getInquiry}>
                            استعلام قیمت
                        </Button>
                    </Fade>
                )}
            </Box>

            <Dialog
                disableScrollLock
                open={replicaLoginDialogOpen}
                onClose={() => setReplicaLoginDialogOpen(false)}
                fullWidth
                maxWidth="xs"
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: '#fff',
                            borderRadius: 3,
                        },
                    },
                }}
            >
                <DialogContent sx={{ px: 4, py: 5, position: 'relative', textAlign: 'center' }}>
                    <ShieldAlert size={44} color={colors.orange[800]} />
                    <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, mt: 2 }}>
                        VPN را خاموش کنید
                    </Typography>
                    <Typography sx={{ color: colors.grey[700], fontSize: 14, lineHeight: 1.9, mt: 1 }}>
                        برای بهبود عملکرد سایت، لطفاً VPN خود را خاموش کنید.
                    </Typography>
                </DialogContent>
            </Dialog>

            <Modal disableScrollLock open={addToCartModalShow} onClose={() => setAddToCartModalShow(false)} slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0,0,0,0.3)'
                    }
                }
            }} >
                <Box sx={{
                    height: '0',
                    width: '0',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    overflow: 'visible',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Paper elevation={24} sx={{
                        flex: 'none',
                        width: {
                            xs: '92vw',
                            md: 425
                        },
                        // height: 400,
                        bgcolor: '#fff',
                        p: 2,
                        position: 'relative',
                    }}>
                        <FlexBox sx={{ justifyContent: 'space-between' }}>
                            <FlexBox>
                                <BadgeCheck size={25} color={colors.green[900]} />

                                <Typography component="span" sx={{
                                    fontSize: 16, ml: 1,
                                    fontWeight: 500
                                }}>
                                    کالا به سبد خرید اضافه شد.
                                </Typography>
                            </FlexBox>
                            <IconButton sx={{ height: 48, width: 48 }} onClick={() => setAddToCartModalShow(false)}>
                                <X />
                            </IconButton>
                        </FlexBox>
                        <Divider sx={{ my: 1 }} />
                        <FlexBox sx={{ p: 2 }}>
                            <MediaV2
                                media_id={showProduct.images[0]?.id}
                                alt={showProduct.images[0]?.alt ?? showProduct.name}
                                width={80} height={64}
                                imageProps={{
                                    fill: true,
                                    style: { objectFit: 'contain' },
                                }} boxProps={{
                                    sx: {
                                        width: 80,
                                        aspectRatio: '1 / 1'
                                    }
                                }} />
                            <Box sx={{ ml: 2 }}>
                                <Typography className="en-num" sx={{
                                    fontSize: 16,
                                    color: colors.grey[900]
                                }}>{showProduct.name}</Typography>
                                <TextOverflow sx={{
                                    fontSize: 13,
                                    fontWeight: 300,
                                    color: colors.grey[700],
                                    '& strong': {
                                        display: 'inline-block',
                                        mr: 0.5,
                                        fontWeight: 400,
                                        color: colors.grey[900],
                                    }
                                }}>
                                    {colorPartsOf(showProduct).map((part, index) => (
                                        <span key={part.slug}>
                                            {index > 0 && ' '}
                                            <strong>{part.title}</strong>
                                            {part.colors.map(i => i.title).join(' , ')}
                                        </span>
                                    ))}
                                </TextOverflow>
                            </Box>
                        </FlexBox>

                        <Button color="primary" variant="outlined" disableElevation fullWidth onClick={goToCart} sx={{
                            height: 48,
                            overflow: 'hidden',
                            '& svg': {
                                position: 'absolute',
                                right: -48,
                                top: 'calc(50% - 10px)',
                                transition: '180ms'
                            },
                            '&:hover': {
                                '& svg': {
                                    right: 16
                                },
                            }
                        }} endIcon={<ArrowLeft color={uiColors.primary} />}>برو به سبد خرید</Button>
                    </Paper>
                </Box>
            </Modal>

        </>
    )
}
